import React, { useEffect, useRef, useState } from "react";
import { TargetImageData } from "../types/TargetImageData";
import { HitDto } from "../types/TargetScannedDataDTOs";
import { CircleMarker } from "../types/CircleMarker";
import { calculateGroupCenterPosition, calculateMeanRadius } from "../utils/statsCalculation";

interface TargetHitsVisualizationProps {
    targetImageData: TargetImageData,
    coordinatesOfHitsRelativeToCenter: HitDto[];
    hitRadius: number
}

export function TargetHitsVisualization(props: TargetHitsVisualizationProps) {

    const targetImageRef = useRef<SVGImageElement>(null);

    const [hitMarkers, setHitMarkers] = useState<CircleMarker[]>([])
    const [targetImageDrawingScaleFactor, setTargetImageDrawingScaleFactor] = useState<number>(1.0);

    const [meanRadius, setMeanRadius] = useState<number>(0);
    const [groupCenterPosition, setGroupCenterPosition] = useState<HitDto>({x: 0, y: 0});

    useEffect(() => {
        setMeanRadius(calculateMeanRadius(props.coordinatesOfHitsRelativeToCenter));
        setGroupCenterPosition(calculateGroupCenterPosition(props.coordinatesOfHitsRelativeToCenter));
    }, []);

    const handleImageLoad = () => {
        if (targetImageRef.current) {
            const targetImageDrawingScaleFactor: number = targetImageRef.current.getBBox().width / props.targetImageData.imageWidth;
            
            const hitMarkers: CircleMarker[] = props.coordinatesOfHitsRelativeToCenter.map((hitDTO: HitDto) => {
                return {
                    x: hitDTO.x * targetImageDrawingScaleFactor * props.targetImageData.scaleFactor,
                    y: hitDTO.y * targetImageDrawingScaleFactor * props.targetImageData.scaleFactor,
                    radius: props.hitRadius * targetImageDrawingScaleFactor * props.targetImageData.scaleFactor
                }
            })

            setTargetImageDrawingScaleFactor(targetImageDrawingScaleFactor);
            setHitMarkers(hitMarkers);
        }
    };

    const getAbsoluteXCoordinate = (xCoordinateRelativeToCenter: number, centerXCoordinate: number): number => 
        xCoordinateRelativeToCenter + centerXCoordinate * targetImageDrawingScaleFactor;

    const getAbsoluteYCoordinate = (yCoordinateRelativeToCenter: number, centerYCoordinate: number): number => 
        -yCoordinateRelativeToCenter + centerYCoordinate * targetImageDrawingScaleFactor;

    return (
        <svg style={{width: "100%", aspectRatio: 1}}>
            <image ref={targetImageRef} 
                href={require(`../images/${props.targetImageData.imageFileName}`)} 
                width="100%" x="0" y="0" 
                preserveAspectRatio="xMinYMin meet" 
                onLoad={handleImageLoad} />
            {hitMarkers.map((hitMarker: CircleMarker) => {
                return (
                    <circle
                        cx={getAbsoluteXCoordinate(hitMarker.x, props.targetImageData.targetCenterX)} 
                        cy={getAbsoluteYCoordinate(hitMarker.y, props.targetImageData.targetCenterY)} 
                        r={hitMarker.radius - hitMarker.radius * 0.25 / 2}
                        fill="green"
                        stroke="darkgreen"
                        opacity={0.7}
                        strokeWidth={hitMarker.radius * 0.25}
                    />
                )
            })}
            <circle
                cx={getAbsoluteXCoordinate(groupCenterPosition.x * props.targetImageData.scaleFactor * targetImageDrawingScaleFactor, props.targetImageData.targetCenterX)}
                cy={getAbsoluteYCoordinate(groupCenterPosition.y * props.targetImageData.scaleFactor * targetImageDrawingScaleFactor, props.targetImageData.targetCenterY)}
                r={meanRadius * targetImageDrawingScaleFactor * props.targetImageData.scaleFactor}
                stroke="purple" 
                strokeWidth={(targetImageRef.current?.getBBox().width || 0) * 0.003}
                fill="none"
            />
            <circle
                cx={getAbsoluteXCoordinate(groupCenterPosition.x * props.targetImageData.scaleFactor * targetImageDrawingScaleFactor, props.targetImageData.targetCenterX)}
                cy={getAbsoluteYCoordinate(groupCenterPosition.y * props.targetImageData.scaleFactor * targetImageDrawingScaleFactor, props.targetImageData.targetCenterY)}
                r={(targetImageRef.current?.getBBox().width || 0) * 0.006}
                stroke="none" 
                fill="purple"
            />      
        </svg>
    )
};